import {CustomFieldDefinition, GPSPosition, ReportRecord} from "@/shared/timr";
import {RecordingMapViewer} from "./recordingMapViewer";
import {htmlEscape} from "../utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {buildPlatformIconTitle, isDriveLog, placePopoverArrowOn, renderBreakTimePopover} from "@/shared/utils/misc";
import {
	formatDate,
	formatDateDay,
	formatDateTime,
	formatDuration,
	formatTime,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {CustomFieldsForRecordType, DeletedStatus, RecordedEntryStatus} from "@/shared/timrTypes";

// L (Leaflet) is loaded globally
declare const L: typeof import("leaflet");

export interface RecordDetailsDialogConfig<T extends ReportRecord> {
	title?: string;
	customFieldDefinitions: CustomFieldDefinition[];
	dialogId: string;
	editRecord: (recording: T) => void;
}

interface GPSPositionMarker {
	marker: L.Marker;
	accuracyCircle?: L.Circle;
}

export abstract class RecordDetailsDialog<T extends ReportRecord> {

	private static PADDING_MAP_TOP_LEFT: [number, number] = [10, 15];
	private static PADDING_MAP_BOTTOM_RIGHT: [number, number] = [10, 75];

	public config: RecordDetailsDialogConfig<T>;
	public map: L.Map;
	protected dom: JQuery;
	protected dialog: BootstrapModal.RecordModal;
	protected recordingMapViewer: RecordingMapViewer;

	protected recordWithCustomFieldValues = false;
	private record: T;
	private isStartPositionDeleted: boolean;
	private isEndPositionDeleted: boolean;
	private isStartPositionAvailable: boolean;
	private isEndPositionAvailable: boolean;
	private identicalCoordinates: boolean;
	private startPosition: GPSPositionMarker;
	private endPosition: GPSPositionMarker;

	constructor(config: RecordDetailsDialogConfig<T>) {
		this.config = config;

		this.dom = $(this.config.dialogId);
		this.init();
	}

	protected initBreakTimePopover() {
		// Only one popover per element, so define it on the parent.
		// content is a function so the popover re-reads the current record on each
		// hover - including the delegated child instance cached on first hover -
		// which keeps it in sync after an edit instead of showing stale content.
		this.dom.popover({
			trigger: "hover",
			placement: "bottom",
			selector: "[data-break-time-popup-area]",
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: `<div class="popover" style="min-width: 330px" role="tooltip"><div class="arrow"></div><div class="popover-content p-5"></div></div>`,
			content: () => renderBreakTimePopover(this.record)
		});

		placePopoverArrowOn(this.dom.find("[data-break-time-popup-area]"), ".recordDetailsDialogBreakTime:visible");
	}

	public show(record: T) {
		this.record = record;
		this.setPlatformInfo(record);
		this.isStartPositionDeleted = record.startPosition?.deletedStatus && record.startPosition.deletedStatus !== DeletedStatus.NOT_DELETED;
		this.isEndPositionDeleted = record.endPosition?.deletedStatus && record.endPosition.deletedStatus !== DeletedStatus.NOT_DELETED;
		this.isStartPositionAvailable = !!record.startPosition?.id && !this.isStartPositionDeleted;
		this.isEndPositionAvailable = !!record.endPosition?.id && !this.isEndPositionDeleted;
		this.setRecordFields(record);
		this.setDateTimeFields(record);
		this.setCustomFields(record);
		this.setModalTitle(record);
		this.toggleAdvancedFieldsContainer(record);
		this.initBreakTimePopover();

		this.identicalCoordinates = this.areGpsPositionCoordinatesIdentical(record.startPosition, record.endPosition);
		this.toggleGpsPositionFields(this.isGpsPositionAvailable());
		if (this.isGpsPositionAvailable()) {
			this.setGpsPositionFields(record);
		}

		this.dialog.show();
	}

	protected setCustomFields(record: T) {
		if (this.isRecordContainingCustomFieldValues(record)) {
			const customFieldContainer = this.dom.find(".recordDetailsDialogAdvancedFieldsContainer");

			this.recordWithCustomFieldValues = false;
			for (const customField of this.config.customFieldDefinitions) {
				const div = $("<div>").addClass("customFieldFlexBox");

				const value = record[`customField${customField.fieldNumber}`];
				if (value) {
					this.recordWithCustomFieldValues = true;
					if (customField.fieldType === CustomFieldsForRecordType.Checkbox) {
						div.append($("<dt>").html("<br>"));
						div.append($("<dd>").html(
							`<label class="checkbox-inline">
									<input type="checkbox" ${value === "true" ? "checked" : ""} disabled />
									${htmlEscape(customField.fieldName)}
							</label>`
						));
					} else {
						div.append($("<dt>").text(customField.fieldName));
						div.append($("<dd>").text(value));
					}
					customFieldContainer.append(div);
				}
			}
			// toggle the custom fields container when no custom field has a value
			customFieldContainer.toggle(this.recordWithCustomFieldValues);
		}
	}

	/**
	 * Initializes the BootstrapModal and adds an event listener for the history table collapse
	 * @protected
	 */
	protected init() {
		this.dialog = new BootstrapModal.RecordModal({
			size: "modal-xl",
			title: this.config.title,
			message: this.dom,
			// necessary for html code in the modal title (don't forget to escape the custom title)
			escapeText: false,
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: [{
				text: "dialog.cancel",
				action: () => {
					this.dialog.close();
				},
				css: "cancelButton btn-default"
			}, {
				text: "report.edit",
				action: () => {
					this.dialog.close();
					this.config.editRecord(this.record);
				},
				css: "editButton btn-primary"
			}],
			onOpen: () => {
				this.updateMapView();
			},
			onClose: () => {
				this.resetRecordDetailsDialogAdvancedFields();
			},
			preventCloseIfLightboxOpen: true
		});

		const collapse = this.dom.find("#recordRevisionTableCollapse");
		collapse
			.on("show.bs.collapse", () => {
				this.dom.find(".recordDetailsRevisionTableTextShow").hide();
				this.dom.find(".recordDetailsRevisionTableTextHide").show();
			})
			.on("hide.bs.collapse", () => {
				this.dom.find(".recordDetailsRevisionTableTextShow").show();
				this.dom.find(".recordDetailsRevisionTableTextHide").hide();
			});
		this.dom.find("a[href='#recordRevisionTableCollapse']").click(() => collapse.collapse("toggle"));
	}

	protected setRecordFields(record: T) {
		const status = this.getStatus(record);
		const statusValue = `<i class="fal fa-status-${status} position-left"></i>` + messages[`reports.filter.status.${status}`];
		this.dom.find(".recordDetailsDialogStatus").html(statusValue);
		this.dom.find(".recordDetailsDialogUser").text(record.userName);
		this.dom.find(".recordDetailsDialogDuration").text(formatDuration(record.durationNet));
	}

	/**
	 * Fills up the description field
	 * Toggles the "No description" badge if no description is available
	 * @param record
	 * @protected
	 */
	protected setDescriptionField(record: T) {
		const descriptionAvailable = record.description && record.description !== "";
		if (descriptionAvailable) {
			const customFieldContainer = this.dom.find(".recordDetailsDialogAdvancedFieldsContainer");
			const descriptionDiv = $("<div>").addClass("descriptionFlexBox");
			descriptionDiv.append($("<dt>").text(messages["record.description"]));
			descriptionDiv.append($("<dd>").addClass("recordDetailsDialogDescription overflow-auto").text(record.description));

			customFieldContainer.append(descriptionDiv);
		}
		this.dom.find(".recordDetailsNoDescriptionBadge").toggle(!descriptionAvailable);
	}

	/**
	 * Toggles the edit button
	 * The edit button shouldn't be visible if the record isn't changeable anymore
	 * @param {Record} record
	 * @param {boolean} showButton
	 * @protected
	 */
	protected toggleEditButton(record: T, showButton: boolean) {
		const editButton = this.dialog.getButtons("editButton");
		editButton.toggle(showButton);
	}

	protected abstract setModalTitle(record: T): void;

	/**
	 * Toggles the the container if tbe record doesn't have custom fields or a description
	 * @param record
	 * @protected
	 */
	protected toggleAdvancedFieldsContainer(record: T) {
		this.dom.find(".recordDetailsDialogAdvancedFieldsContainer")
			.toggle(this.recordWithCustomFieldValues || record.description !== "");
	}

	/**
	 * Removes the custom fields and the description of the parent container
	 * These fields are appended dynamically
	 * @protected
	 */
	protected resetRecordDetailsDialogAdvancedFields() {
		this.dom.find("#recordRevisionTableCollapse").collapse("hide");
		this.dom.find(".recordDetailsDialogAdvancedFieldsContainer").empty();
	}

	/**
	 * Fills up address and latitude and longitude of the GPS positions
	 * Some records may not contain a formatted address, in this case we show only the latitude and longitude in a different style
	 * @param {GPSPosition} gpsPosition Start or end position of the record
	 * @param {JQuery} field Start or end DOM
	 * @private
	 */
	protected setAddressAndLatLngFields(gpsPosition: GPSPosition, field: JQuery) {
		const accuracyValue = $.isNumeric(gpsPosition.horizontalAccuracy) ?
			Number(gpsPosition.horizontalAccuracy).toFixed(0) + " m" : messages["position.accuracy.unknown"];

		if (gpsPosition.formattedAddress) {
			field.find(".formatted-address")
				.show()
				.html(gpsPosition.formattedAddress.replace(/,/g, "<br>"));
			field.find(".longitude-latitude").empty();
			field.find(".accuracy")
				.html(gpsPosition.latitude + ", " + gpsPosition.longitude + " (" + accuracyValue + ")")
				.prop("title", messages["record.details.latitude.longitude.tooltip"] + ", " + messages["position.accuracy"]);
		} else {
			field.find(".formatted-address").hide();
			field.find(".longitude-latitude")
				.text(gpsPosition.latitude + ", " + gpsPosition.longitude)
				.prop("title", messages["record.details.latitude.longitude.tooltip"]);
			field.find(".accuracy")
				.text(messages["position.accuracy"] + ": " + accuracyValue)
				.prop("title", "");
		}
		const timestamp = parseISOZonedDateTime(gpsPosition.timestamp);
		field.find(".timestamp")
			.empty()
			.text(formatDateTime(timestamp));
		if (globals.showTimeZone) {
			field.find(".timestamp")
				.append(`<span class="text-timezone"> ${timestamp?.offset ?? ""}</span>`);
		}
	}

	private initGpsPositionMarkers() {
		this.startPosition = {marker: null, accuracyCircle: null};
		this.endPosition = {marker: null, accuracyCircle: null};
	}

	private setPlatformInfo(record: T) {
		this.dom.find(".start-platform-info").toggle(record.startPlatform !== null);
		if (record.startPlatform) {
			const startPlatformIconTitle = buildPlatformIconTitle(record.startPlatform, record, true);
			this.dom.find(".start-platform-info")
				.html(`<i class="fal fa-platform-${record.startPlatform} position-right"></i>`)
				.prop("title", startPlatformIconTitle);
		}

		this.dom.find(".end-platform-info").toggle(record.endPlatform !== null);
		if (record.endPlatform) {
			const endPlatformIconTitle = buildPlatformIconTitle(record.endPlatform, record, false);
			this.dom.find(".end-platform-info")
				.html(`<i class="fal fa-platform-${record.endPlatform} position-right"></i>`)
				.prop("title", endPlatformIconTitle);
		}
	}

	/**
	 * Fills up the fields which are visible when the recording contains at least one GPS position
	 * @param {Record} record
	 * @private
	 */
	private setGpsPositionFields(record: T) {
		if (!this.map) {
			this.map = this.initGpsPositionMap(this.dom.find(".gps-position-map"));
			this.initGpsPositionMapListeners();
		}
		this.renderGpsPositionMarkers(record);
		this.toggleOnePositionAvailableInfo(record);

		if (this.isStartPositionAvailable) {
			this.setAddressAndLatLngFields(record.startPosition, this.dom.find(".start-address-column"));
		}
		if (this.isEndPositionAvailable) {
			this.setAddressAndLatLngFields(record.endPosition, this.dom.find(".end-address-column"));
		}
	}

	/**
	 * If a gps position is available, the dialog should show a map with the position(s)
	 * @param {boolean} gpsPositionAvailable true or false if at least one gps position is available
	 */
	private toggleGpsPositionFields(gpsPositionAvailable: boolean) {
		const modalWithGps = this.dom.find(".gps-position-available");
		const modalWithoutGps = this.dom.find(".gps-position-not-available");

		modalWithGps.toggle(gpsPositionAvailable);
		modalWithoutGps.toggle(!gpsPositionAvailable);

		// if there is no map on the top of the dialog, there is more space for the - scrollable - note field
		this.dom.find(".recordDetailsDialogDescription").css("max-height", gpsPositionAvailable ? "21vh" : "42vh");
	}

	/**
	 * Show an info text if only start or end position is available
	 * @param {T} record
	 * @private
	 */
	private toggleOnePositionAvailableInfo(record: T) {
		if (this.isStartPositionAvailable && this.isEndPositionAvailable) {
			this.dom.find(".one-position-info").hide();
		} else if (!this.isEndPositionAvailable && !this.isEndPositionDeleted && this.isStartPositionAvailable && record.endPlatform) {
			this.dom.find(".gps-position-panel-body-end .one-position-info > span").text(messages[`record.details.onePosition.${record.endPlatform}`]);
			this.dom.find(".gps-position-panel-body-end .one-position-info").show();

			this.dom.find(".gps-position-panel-body-start .one-position-info").hide();
			this.dom.find(".gps-position-panel-body-end .gps-position-available").hide();
		} else if (!this.isStartPositionAvailable && !this.isStartPositionDeleted && this.isEndPositionAvailable && record.startPlatform) {
			this.dom.find(".gps-position-panel-body-start .one-position-info > span").text(messages[`record.details.onePosition.${record.startPlatform}`]);
			this.dom.find(".gps-position-panel-body-start .one-position-info").show();

			this.dom.find(".gps-position-panel-body-end .one-position-info").hide();
			this.dom.find(".gps-position-panel-body-start .gps-position-available").hide();
		} else if (!this.isEndPositionAvailable && this.isEndPositionDeleted && this.isStartPositionAvailable && record.endPlatform) {
			this.dom.find(".gps-position-panel-body-end .one-position-info > span").text(messages[`record.details.onePosition.deleted`]);
			this.dom.find(".gps-position-panel-body-end .one-position-info").show();

			this.dom.find(".gps-position-panel-body-start .one-position-info").hide();
			this.dom.find(".gps-position-panel-body-end .gps-position-available").hide();
		} else if (!this.isStartPositionAvailable && this.isStartPositionDeleted && this.isEndPositionAvailable && record.startPlatform) {
			this.dom.find(".gps-position-panel-body-start .one-position-info > span").text(messages[`record.details.onePosition.deleted`]);
			this.dom.find(".gps-position-panel-body-start .one-position-info").show();

			this.dom.find(".gps-position-panel-body-end .one-position-info").hide();
			this.dom.find(".gps-position-panel-body-start .gps-position-available").hide();
		}
	}

	private setDateTimeFields(record: T) {
		const startDate = parseISOZonedDateTime(record.start);
		const endDate = parseISOZonedDateTime(record.end);
		const timeZoneStringStartDate = (globals.showTimeZone || (record.startTimezone !== record.endTimezone)) ? record.startTimezone : "";
		const timeZoneStringEndDate = (globals.showTimeZone || (record.startTimezone !== record.endTimezone)) ? record.endTimezone : "";

		if (this.isGpsPositionAvailable()) {
			this.buildGpsPositionAvailableDateTimeColumn(this.dom.find(".start-date-time-column"), record, startDate, timeZoneStringStartDate);
			this.buildGpsPositionAvailableDateTimeColumn(this.dom.find(".end-date-time-column"), record, endDate, timeZoneStringEndDate);
		} else {
			this.buildGpsPositionNotAvailableDateTimeColumn(this.dom.find(".date-time-column"), record, startDate, endDate, timeZoneStringStartDate, timeZoneStringEndDate);
		}
	}

	private buildGpsPositionAvailableDateTimeColumn(dateField: JQuery, record: T, dateTime: Temporal.ZonedDateTime | null, timeZoneStringDate: string) {
		const changedIcon = record.changed ? `<i class="fal fa-pen" title="${messages["report.column.changed.title"]}"></i>` : "";
		dateField.html(`<span class="text-size-large">${formatTime(dateTime)}</span><span class="text-timezone"> ${timeZoneStringDate}</span>
										<div>
											${formatDateDay(dateTime)}, <span class="text-size-small">${formatDate(dateTime)}</span>
										</div>`);
		dateField.siblings(".editedInfo").html(changedIcon);
	}

	private buildGpsPositionNotAvailableDateTimeColumn(dateField: JQuery, record: T, startDate: Temporal.ZonedDateTime | null, endDate: Temporal.ZonedDateTime | null, timeZoneStringStartDate: string, timeZoneStringEndDate: string) {
		const startPlatformIconTitle = buildPlatformIconTitle(record.startPlatform, record, true);
		const endPlatformIconTitle = buildPlatformIconTitle(record.endPlatform, record, false);
		const changedIcon = record.changed ? `<i class="fal fa-pen ml-15" title="${messages["report.column.changed.title"]}"></i>` : "";

		if (formatDate(startDate) === formatDate(endDate)) {
			dateField.html(`<div class="flexbox-container">
											<i class="fal fa-platform-${record.startPlatform} fa-fw" title="${startPlatformIconTitle}"></i>
											<div>
												<span>
													<span>${formatTime(startDate)}</span>
													<span class="text-timezone"> ${timeZoneStringStartDate}</span>
												</span>
												<i class="time-separation-symbol ml-10 mr-10"></i>
												<span>
													<span>${formatTime(endDate)}
													<span class="text-timezone"> ${timeZoneStringEndDate} </span>
												</span>
												<div>
													${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
												</div>
											</div>
											<i class="fal fa-platform-${record.endPlatform} ml-10" title="${endPlatformIconTitle}"></i>
											${changedIcon}
										</div>`);
		} else {
			dateField.html(`<div class="flexbox-container">
											<i class="fal fa-platform-${record.startPlatform} fa-fw" title="${startPlatformIconTitle}"></i>
											<div>
												${formatTime(startDate)}<span class="text-timezone"> ${timeZoneStringStartDate}</span>
												<div>
													${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
												</div>
											</div>
											<div>
												<i class="time-separation-symbol ml-5 mr-10"></i>
											</div>
											<div>
												${formatTime(endDate)}<span class="text-timezone"> ${timeZoneStringEndDate}</span>
												<div>
													${formatDateDay(endDate)}, <span class="text-size-small">${formatDate(endDate)}</span>
												</div>
											</div>
											<i class="fal fa-platform-${record.endPlatform} ml-10" title="${endPlatformIconTitle}"></i>
											${changedIcon}
										</div>`);
		}
	}

	/**
	 * Update the map view and fit bounds of the gps positions
	 * @private
	 */
	private updateMapView() {
		if (this.map) {
			this.map.invalidateSize(true);

			this.fitMapBounds();
		}
	}

	private initGpsPositionMapListeners() {
		window.addEventListener("resize", () => this.map.invalidateSize(true));

		const centerGpsPositionMarkersButton = this.dom.find("#centerGpsPositionMarkersButtonContainer a");
		centerGpsPositionMarkersButton.click(() => {
			this.fitMapBounds();
		});

		this.map.on("dragstart zoomstart", (e) => {
			centerGpsPositionMarkersButton.toggleClass("gps-positions-centered", false);
		});

		this.dom.find(".gps-position-start-column").click(() => {
			this.flyTo(this.record.startPosition);
			if (this.identicalCoordinates || !this.isEndPositionAvailable) {
				centerGpsPositionMarkersButton.toggleClass("gps-positions-centered", true);
			}
		});

		this.dom.find(".gps-position-end-column").click(() => {
			this.flyTo(this.record.endPosition);
			if (this.identicalCoordinates || !this.isStartPositionAvailable) {
				centerGpsPositionMarkersButton.toggleClass("gps-positions-centered", true);
			}
		});
	}

	private initGpsPositionMap(mapContainer: JQuery): L.Map {
		const map = L.map(mapContainer[0], {
			zoom: 12,
			attributionControl: false,
			layers: [L.tileLayer("https://{s}.tile.osm.org/{z}/{x}/{y}.png", {
				attribution: "&copy; <a href=\"http://osm.org/copyright\">OpenStreetMap</a> contributors",
				zIndex: 1
			})],
			zoomControl: true,
			dragging: true,
			touchZoom: false,
			doubleClickZoom: false,
			scrollWheelZoom: true,
			trackResize: false
		});
		L.control.attribution({
			position: "topright"
		}).addTo(map);

		return map;
	}

	/**
	 * Adds the following elements for each GPS location
	 * - Marker
	 * - Circles for the horizontal accuracy
	 * - Polyline for drive path tracks to the map
	 * @private
	 */
	private renderGpsPositionMarkers(record: T) {
		this.initGpsPositionMarkers();
		this.map.eachLayer((layer) => {
			if (layer instanceof L.Marker || layer instanceof L.Circle || layer instanceof L.Polyline) {
				this.map.removeLayer(layer);
			}
		});

		const startPosition = record.startPosition;
		const endPosition = record.endPosition;
		this.recordingMapViewer = new RecordingMapViewer({
			map: this.map,
			fitBoundsOptions: {
				paddingTopLeft: RecordDetailsDialog.PADDING_MAP_TOP_LEFT,
				paddingBottomRight: RecordDetailsDialog.PADDING_MAP_BOTTOM_RIGHT
			}
		});
		if (isDriveLog(record) && record.hasTrack) {
			this.recordingMapViewer.loadTrack(record, {
				trackLoadedCallback: (latlngs, dl, config) => {
					setTimeout(() => this.recordingMapViewer.displayTrack(this.map, latlngs, dl, config), 100);
				}
			});
		} else {
			const startLatLng = L.latLng(startPosition.latitude, startPosition.longitude);
			const endLatLng = L.latLng(endPosition.latitude, endPosition.longitude);
			if (this.isStartPositionAvailable) {
				this.startPosition.marker = !this.identicalCoordinates ?
					this.recordingMapViewer.displayStartMarker(startLatLng) : this.recordingMapViewer.displayIdenticalMarker(startLatLng);
				this.startPosition.accuracyCircle = startPosition.horizontalAccuracy ?
					this.recordingMapViewer.displayStartMarkerCircle(startLatLng, startPosition.horizontalAccuracy) : null;
			}
			if (this.isEndPositionAvailable) {
				if (!this.identicalCoordinates) {
					this.endPosition.marker = this.recordingMapViewer.displayEndMarker(endLatLng);
				}
				const dashedAccuracyCircle = this.identicalCoordinates && startPosition.horizontalAccuracy === endPosition.horizontalAccuracy;
				this.endPosition.accuracyCircle = endPosition.horizontalAccuracy ?
					this.recordingMapViewer.displayEndMarkerCircle(endLatLng, endPosition.horizontalAccuracy, dashedAccuracyCircle) : null;

			}
			this.fitMapBounds();
		}
	}

	/**
	 * Adjusts the map boundaries that all elements are visible in the map view
	 * Such as GPS locations, their accuracy circles or drive path tracks
	 * @private
	 */
	private fitMapBounds() {
		if (this.isGpsPositionAvailable()) {
			if (isDriveLog(this.record) && this.record.hasTrack) {
				this.recordingMapViewer.fitBounds(this.map);
			} else {
				const startHorizontalAccuracy = this.startPosition.accuracyCircle ? this.startPosition.accuracyCircle.getRadius() * 2 : 0;
				const startBounds = this.isStartPositionAvailable ? this.startPosition.marker.getLatLng().toBounds(startHorizontalAccuracy) : null;
				const endHorizontalAccuracy = this.endPosition.accuracyCircle ? this.endPosition.accuracyCircle.getRadius() * 2 : 0;
				const endBounds = this.isEndPositionAvailable && !this.identicalCoordinates ? this.endPosition.marker.getLatLng().toBounds(endHorizontalAccuracy) : null;

				let north: number, east: number, south: number, west: number;
				if (this.isStartPositionAvailable && this.isEndPositionAvailable && !this.identicalCoordinates) {
					north = Math.max(startBounds.getNorth(), endBounds.getNorth());
					east = Math.max(startBounds.getEast(), endBounds.getEast());
					south = Math.min(startBounds.getSouth(), endBounds.getSouth());
					west = Math.min(startBounds.getWest(), endBounds.getWest());
				} else {
					const startOrEndBounds = this.isStartPositionAvailable ? startBounds : endBounds;
					north = startOrEndBounds.getNorth();
					east = startOrEndBounds.getEast();
					south = startOrEndBounds.getSouth();
					west = startOrEndBounds.getWest();
				}

				this.map.fitBounds(L.latLngBounds(L.latLng(south, west), L.latLng(north, east)), {
					paddingTopLeft: RecordDetailsDialog.PADDING_MAP_TOP_LEFT,
					paddingBottomRight: RecordDetailsDialog.PADDING_MAP_BOTTOM_RIGHT
				});
			}
			const centerGpsPositionMarkersButton = this.dom.find("#centerGpsPositionMarkersButtonContainer a");
			setTimeout(() => centerGpsPositionMarkersButton.toggleClass("gps-positions-centered", true), 100);
		}
	}

	/**
	 * Sets the view to the transmitted gpsPosition
	 * It is done with an animation
	 * @param {GPSPosition} gpsPosition
	 * @private
	 */
	private flyTo(gpsPosition: GPSPosition) {
		const horizontalAccuracy = gpsPosition.horizontalAccuracy ? gpsPosition.horizontalAccuracy * 2 : 0;
		const latLngBounds = L.latLng(gpsPosition.latitude, gpsPosition.longitude);
		this.map.flyToBounds(latLngBounds.toBounds(horizontalAccuracy), {
			paddingTopLeft: RecordDetailsDialog.PADDING_MAP_TOP_LEFT,
			paddingBottomRight: RecordDetailsDialog.PADDING_MAP_BOTTOM_RIGHT
		});
	}

	private isGpsPositionAvailable(): boolean {
		return this.isStartPositionAvailable || this.isEndPositionAvailable;
	}

	/**
	 * Checks if the longitude and latitude values of the start and end position coordinates are identical
	 * @param {GPSPosition} gpsPosition
	 * @private
	 */
	private areGpsPositionCoordinatesIdentical(startPosition: GPSPosition, endPosition: GPSPosition): boolean {
		return this.isStartPositionAvailable && this.isEndPositionAvailable &&
			startPosition.latitude === endPosition.latitude && startPosition.longitude === endPosition.longitude;
	}

	private isRecordContainingCustomFieldValues(record: T): boolean {
		return this.config.customFieldDefinitions.some(customField => {
			return !!record[`customField${customField.fieldNumber}`];
		});
	}

	protected abstract getStatus(record: T): RecordedEntryStatus;
}
